/* eslint-disable no-unused-expressions */
/** @jsx jsx */

import {
  ComponentProps,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { range } from 'lodash';
import { format, subDays } from 'date-fns';

import { Colors, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import ExtendedIcon from 'app/developer-v3/components/ExtendedIcon';
import MultiSelectDropdown from 'app/developer-v3/components/MultiSelectDropdown';
import { CommonMultiItemsChart } from 'app/developer-v3/components/CommonChart/CommonMultiItemsChart';
import { TimePeriodDays } from 'app/developer-v3/hooks/useIntegrationInsights';
import { useQueryInsightsIntegrationTriggersActionsDaily } from 'app/developer-v3/hooks';
import {
  IntegrationTriggersActionsDaily,
  MethodType,
} from 'app/developer-v3/hooks/useQueryInsightsIntegrationTriggersActionsDaily';

import DateRangeDropdown from '../DateRangeDropdown';
import { BaseTile } from './BaseTile';
import {
  defaultInitialSelectedItem,
  escapeHTML,
  timePeriodDaysItems,
} from '../utils';

const html = String.raw;

const styleRoot = (methodType: MethodType) => css`
  display: flex;
  flex-direction: column;
  background-color: ${methodType === 'triggers'
    ? Colors.GrayWarm2
    : Colors.PrimeWhite};
  border-top: 1px solid ${Colors.GrayWarm4};
`;

type TooltipType = { role: string; type: string; p: { html: boolean } };
interface ZapActivationRateTileProps {
  appId: string;
  title: string;
  tipContent?: ReactNode;
  tipContentStyle?: React.CSSProperties;
  tooltipContent: ReactNode;
  type: MethodType;
  dataTestId?: string;
}

// By default google charts shows every label without overlapping. However text will wrap and can causing other issues
// See docs https://developers.google.com/chart/interactive/docs/gallery/linechart#:~:text=hAxis.textStyle.fontSize-,hAxis.showTextEvery,-How%20many%20horizontal
const ShowAxisLabel = {
  EVERY_LABEL: 1,
  EVERY_OTHER_LABEL: 2,
  EVERY_THIRD_LABEL: 3,
  EVERY_FOURTH_LABEL: 4,
  EVERY_FIFTH_LABEL: 5,
} as const;

type MultiSelectItems = ComponentProps<typeof MultiSelectDropdown>['items'];

export default function ZapActivationRateTile({
  appId,
  title,
  tooltipContent,
  tipContent,
  tipContentStyle,
  type: methodType,
  dataTestId,
}: ZapActivationRateTileProps) {
  const [hasRenderError, setHasRenderError] = useState(false);
  const [selectedTriggersActions, setSelectedTriggersActions] = useState<
    MultiSelectItems
  >([]);
  const [zapActivationRatePeriod, setZapActivationRatePeriod] = useState<
    TimePeriodDays
  >(TimePeriodDays.Last_Thirty_days);

  const { data, error } = useQueryInsightsIntegrationTriggersActionsDaily(
    appId,
    methodType
  );

  useEffect(() => {
    if (data?.[methodType]) {
      setSelectedTriggersActions(
        data?.[methodType]
          .filter(
            e =>
              e.rank_group_by_method_type === 'top_ranked' &&
              range(1, 6).includes(e.rank_created_nodes_by_method_type)
          )
          .map(e => ({ label: e.method_name, value: e.method_name }))
      );
    }
  }, [data, methodType]);

  const itemType = methodType === 'triggers' ? 'Trigger' : 'Action';

  const yAxisLabel = useMemo(
    () => '7 day average % activated within 24 hours',
    []
  );
  const xAxisLabel = `${itemType} created date`;

  // values for determining horizontal labeling to avoid text wrapping and a cleaner look
  const hAxisLabelConfig = useMemo(() => {
    if (zapActivationRatePeriod === TimePeriodDays.Last_Thirty_days) {
      return ShowAxisLabel.EVERY_OTHER_LABEL;
    } else if (zapActivationRatePeriod === TimePeriodDays.Last_Ninety_days) {
      return ShowAxisLabel.EVERY_FIFTH_LABEL;
    }
    return ShowAxisLabel.EVERY_LABEL;
  }, [zapActivationRatePeriod]);

  const generateTooltip = useCallback(
    (itemName: string, itemValue: number, date: Date) => {
      return html`
        <div>
          <p>${escapeHTML(itemType)}: ${escapeHTML(itemName)}</p>
          <p>${escapeHTML(date.toLocaleDateString().replace(/\//g, '-'))}</p>
          <p>
            Zap activation rate: ${escapeHTML(String(Math.round(itemValue)))}%
          </p>
        </div>
      `;
    },
    [itemType]
  );

  const chartInfo = useMemo(() => {
    const dataByMethod = data?.[methodType]; // get daily activation rate data grouped by trigger/action
    if (!dataByMethod) {
      return [];
    }

    const chartHeader: Array<string | TooltipType> = ['Date'];

    /*
      This function takes in data organized like this:

          {
            method_name: "Action 1",
            daily_data: [
              {
                date: "<ISO8601 date time string>",
                avg_percentage_activated_last_7_days: 0.712,
                ...
              },
              ...
            ],
            ...
          }

      And organizes it like this:

      [
        ['Date', 'Action 1', { tooltip_object }, 'Action 2', { tooltip_object }],
        [new Date("..."), 1, "<HTML tooltip for action 1>", 2, <HTML tooltip for action 2>],
        ...
      ]
    */

    // Date --> data points at that date
    const map = new Map<string, [Date, ...Array<number | string>]>();

    // Create an array of "chartDates": days for which we will display data in the chart.
    // (Determined by the user's selection of "Last 7 Days", "Last 30 Days", or "Last 90 Days")
    // The dates will be strings formatted as YYYY-MM-DD
    const chartDates = [];
    const firstDateInRange = subDays(new Date(), 1); // the first date should be "yesterday"
    for (let i = 0; i < zapActivationRatePeriod; i += 1) {
      chartDates.push(format(subDays(firstDateInRange, i), 'yyyy-MM-dd'));
    }

    // Iterate over the selected methods (triggers/actions)
    for (const selectedMethod of selectedTriggersActions) {
      const dataGroupForMethod = dataByMethod.find(
        d => d.method_name === selectedMethod.label
      );
      if (!dataGroupForMethod) {
        continue;
      }

      // Add the trigger/action name and a tooltip to the chart's header
      chartHeader.push(dataGroupForMethod.method_name, {
        role: 'tooltip',
        type: 'string',
        p: {
          html: true,
        },
      });

      // Iterate over the chart dates for this method
      for (const expectedDateString of chartDates) {
        // Create a Date object that matches the date string (YYYY-MM-DD), but in local time
        // Just calling new Date('YYYY-MM-DD') will create a date with UTC time, which can cause the chart to display incorrect dates
        const localTimeOffsetMinutes = new Date().getTimezoneOffset();
        const expectedDate = new Date(
          new Date(expectedDateString).getTime() +
            localTimeOffsetMinutes * 60000
        );

        // Find data for the expected date
        const datePointForData = dataGroupForMethod.daily_data.find(
          dailyDataPoint => dailyDataPoint.date === expectedDateString
        );

        // If there is no data for this method on the expected date, then create a "null" data point to fill the gap
        const chartDataPoint: Partial<IntegrationTriggersActionsDaily> = datePointForData
          ? datePointForData
          : {
              date: expectedDateString,
              avg_percentage_activated_last_7_days: null,
            };

        // Add the data point to our map of chart data
        let array = map.get(expectedDateString);
        if (!array) {
          array = [expectedDate];
          map.set(expectedDateString, array);
        }
        array.push(
          // convert integer percentages to decimal percentages (or null), because that's what google charts needs
          chartDataPoint.avg_percentage_activated_last_7_days != null // check for null or undefined, but allow 0
            ? chartDataPoint.avg_percentage_activated_last_7_days / 100
            : NaN,
          // include a tooltip for this data point
          generateTooltip(
            dataGroupForMethod.method_name,
            chartDataPoint.avg_percentage_activated_last_7_days,
            expectedDate
          )
        );
      }
    }

    return [chartHeader, ...map.values()];
  }, [
    data,
    generateTooltip,
    selectedTriggersActions,
    methodType,
    zapActivationRatePeriod,
  ]);

  return (
    <figure css={styleRoot(methodType)}>
      <BaseTile
        dataTestId={dataTestId}
        dropdowns={[
          <MultiSelectDropdown
            items={data?.[methodType].map(({ method_name }) => ({
              value: method_name,
              label: method_name,
            }))}
            itemsType={methodType}
            maxSelectedItems={7}
            onChange={setSelectedTriggersActions}
            placeholder={`No ${methodType} selected`}
            selectedItems={selectedTriggersActions}
          />,
          <div style={{ flex: 'none' }}>
            <DateRangeDropdown
              initialSelectedItem={defaultInitialSelectedItem}
              items={timePeriodDaysItems}
              onSelect={period =>
                setZapActivationRatePeriod(period as TimePeriodDays)
              }
            />
          </div>,
        ]}
        headerContent={title}
        icon={
          <ExtendedIcon
            color="PrimeBlack"
            name={methodType === 'actions' ? 'action' : 'trigger'}
            size={24}
          />
        }
        isError={Boolean(error) || hasRenderError}
        // use the chart loader prop to handle display of loading state
        isLoading={false}
        isNoData={!(chartInfo && chartInfo.length > 1)}
        style={{ height: '100%' }}
        tipContent={tipContent}
        tipContentStyle={tipContentStyle}
        toolTipContent={
          <Text color="PrimeWhite" tag="p" type="Body1">
            {tooltipContent}
          </Text>
        }
        tooltipPosition="southeast"
      >
        <CommonMultiItemsChart
          chartType="LineChart"
          colors={[
            '#22357B',
            '#DE4867',
            '#40A6FF',
            '#0E5D4E',
            '#CCBB7B',
            '#F58F27',
            '#B027BB',
          ]}
          data={chartInfo}
          hAxisFormat="MMM dd"
          hAxisLabelConfig={hAxisLabelConfig}
          optionOverrides={{ vAxis: { maxValue: 1, minValue: 0 } }}
          setHasRenderError={setHasRenderError}
          vAxisFormat="percent"
          xAxisLabel={xAxisLabel}
          yAxisLabel={yAxisLabel}
        />
      </BaseTile>
    </figure>
  );
}
